/**
 * @fileoverview This file houses functions that can be used for
 * validation of form fields.
 *
 * Note that these functions should return a string error message
 * when they fail, and `undefined` when they pass.
 */

const SUCCESS = undefined

export const hasLengthAtLeast = (value, values, minimumLength) => {
  const message = `Must contain at least ${minimumLength} character(s).`

  if (!value || value.length < minimumLength) {
    return message
  }

  return SUCCESS
}

export const hasLengthAtMost = (value, values, maximumLength) => {
  if (value && value.length > maximumLength) {
    const message = `Must not exceed ${maximumLength} character(s).`

    return message
  }

  return SUCCESS
}

export const hasLengthExactly = (value, values, length) => {
  if (value && value.length !== length) {
    const message = `Must contain exactly ${length} character(s).`

    return message
  }

  return SUCCESS
}

/**
 * isRequired is provided here for convenience but it is inherently ambiguous and therefore we don't recommend using it.
 * Consider using more specific validators such as `hasLengthAtLeast` or `mustBeChecked`.
 */
export const isRequired = (value) => {
  const FAILURE = "This field is required."

  // The field must have a value (no null or undefined) and
  // if it's a boolean, it must be `true`.
  if (!value) { return FAILURE }

  // If it is a number or string, it must have at least one character of input (after trim).
  const stringValue = String(value).trim()
  const measureResult = hasLengthAtLeast(stringValue, null, 1)

  if (measureResult) { return FAILURE }

  return SUCCESS
}

export const mustBeChecked = (value) => {
  const message = "Must be checked."

  if (!value) { return message }

  return SUCCESS
}

export const validateRegionCode = (value, values, countries) => {
  const countryCode = process.env.NEXT_PUBLIC_DEFAULT_COUNTRY_CODE
  const country = countries.find(({
    id 
  }) => id === countryCode)

  if (!country) {
    const invalidCountry = `Country "${countryCode}" is not an available country.`

    return invalidCountry
  }

  const {
    available_regions: regions 
  } = country

  if (!(Array.isArray(regions) && regions.length)) {
    const invalidRegions = `Country "${countryCode}" does not contain any available regions.`

    return invalidRegions
  }

  const region = regions.find(({
    code 
  }) => code === value)

  if (!region) {
    const invalidAbbrev = `State "${value}" is not a valid state abbreviation.`

    return invalidAbbrev
  }

  return SUCCESS
}

export const validatePassword = (value) => {
  if (value.length < 6) {
    const message = "A password must contain at least 6 characters."

    return message
  }

  return SUCCESS
}

export const isEqualToField = (value, values, fieldKey) => {
  const message = `${fieldKey} must match.`

  return value === values[fieldKey] ? SUCCESS : message
}

export const isNotEqualToField = (value, values, fieldKey) => {
  const message = `${fieldKey} must be different`

  return value !== values[fieldKey] ? SUCCESS : message
}

export const validateEmail = (value, fieldKey) => {
  const message =
        "Please enter a valid email address (Ex: johndoe@domain.com)."

  return /^[A-Za-zd0-9]+([-_.][A-Za-zd0-9]+)*@([A-Za-zd]+[-.])+[A-Za-zd]{2,5}$/.test(value)
    ? SUCCESS
    : message
}

export const validateEmail2 = (value, fieldKey) => {
  const message =
        "The email address is incorrect."

  return /^[A-Za-zd0-9]+([-_.][A-Za-zd0-9]+)*@([A-Za-zd]+[-.])+[A-Za-zd]{2,5}$/.test(value)
    ? SUCCESS
    : message
}


export const isDynamicRequired = (value, values, applicationobj) => {
  if (applicationobj.length < 1 && !applicationobj) {
    return SUCCESS
  }

  const FAILURE = "This is a required field."

  // The field must have a value (no null or undefined) and
  // if it's a boolean, it must be `true`.
  if (!value && !applicationobj[1][applicationobj[0]]) { return FAILURE }

  // If it is a number or string, it must have at least one character of input (after trim).
  const stringValue = String(value).trim()
  const measureResult = hasLengthAtLeast(stringValue, null, 1)

  if (measureResult) { return FAILURE }

  return SUCCESS
}

export const isEqualRangeToField = (value, values, range) => {
  const minValue = range.split("-")[0]
  const maxValue = range.split("-")[1]
  const message = "The value is not within the specified range."

  return Number(value) < Number(minValue) || Number(value) > Number(maxValue)
    ? message
    : SUCCESS
}

export const emailAstrict = (value, values, fieldKey) => {
  const message =
        "Email is not supported, we recommend using Gmail or Outlook."

  const disableEmail = ["qq", "163", "126", "sina"]

  return disableEmail.indexOf(value.toLowerCase().split("@")[1].split(".")[0]) < 0
    ? SUCCESS
    : message
}

export const isSelectAppType = (value, values, application) => {
  let count = 0

  Object.keys(application).forEach((key) => {
    application[key] ? count++ : null
  })
  const message = "Please select at least one."

  return count < 6 ? SUCCESS : message
}
